<template>
  <div class="pdf-viewer" data-locator="pdf-container">
    <pdfjs-viewer-element
      ref="pdfElem"
      class="pdf-container"
      :class="{
        'out-of-bounds': !initialized,
        loading: !initialized,
      }"
      :locale="$i18n.locale"
      :viewer-path="viewerPath"
      :viewer-css-theme="1"
    >
    </pdfjs-viewer-element>

    <Teleport v-if="leftToolbarElement" :to="leftToolbarElement">
      <slot name="left-side" :instance="getPdfAppInstance()">
        <AppTooltip :offset="-20">
          <template #activator="{ props }">
            <app-button
              v-bind="props"
              type='icon'
              class="mr-2"
              size="small"
              data-locator="toggle-thumbs-button"
              :class="{ '--is-active': pageMode === 'thumbs' }"
              @click="togglePageMode"
            >
              <app-icon-ds
                icon="files"
                size="xs"
              />
            </app-button>
          </template>
          <span class="text--small">{{ $t('common.DocumentPdfViewer.pageModeLabel') }}</span>
        </AppTooltip>
        <span class="text--small">{{ $t('common.DocumentPdfViewer.pageLabel') }}</span>
        <AppTextField
          :model-value="currentPage"
          data-locator="page-input"
          outlined
          dense
          small
          class="mx-2"
          @blur="setPageFromEvent"
          @keydown.enter="setPageFromEvent"
        />
        <span class="mr-2 text--small" data-locator="pages-count">
          {{ $t('common.DocumentPdfViewer.pageRemainder', pageCount) }}
        </span>
        <slot name="left-side-append" :instance="getPdfAppInstance()"></slot>
      </slot>
    </Teleport>
    <Teleport v-if="middleToolbarElement" :to="middleToolbarElement">
      <slot name="middle-side" :instance="getPdfAppInstance()"></slot>
    </Teleport>
    <Teleport v-if="rightToolbarElement" :to="rightToolbarElement">
      <slot name="right-side" :instance="getPdfAppInstance()">
        <AppTooltip :offset="-20">
          <template #activator="{ props }">
            <app-button
              v-bind="props"
              data-locator="zoom-out-button"
              class="mr-1"
              type='icon'
              size="small"
              @click="zoomOut()"
            >
              <app-icon-ds
                icon="circle-minus"
                size="xs"
              />
            </app-button>
          </template>
          <span>{{ $t('common.DocumentPdfViewer.zoomOut') }}</span>
        </AppTooltip>
        <AppSelect
          v-model="zoomLevel"
          data-locator="zoom-input"
          :items="zoomLevelItems"
          outlined
          dense
          small
          no-label
          :menuProps="{
            nudgeTop: 50,
            bottom: true,
            offset: menuOffset,
            transition: menuTransition,
          }"
          class="mr-1"
          :suffix="zoomLevel !== 'auto' ? '%' : null"
          @update:model-value="setZoomLevel($event)"
        >
          <template #item="{ props, item }">
            <v-list-item v-if="item.value !== 'auto'" v-bind="props" :title="props.title + ' %'" >
            </v-list-item>
            <v-list-item v-else v-bind="props" :title="props.title">
            </v-list-item>
          </template>
        </AppSelect>
        <AppTooltip :offset="-20">
          <template #activator="{ props }">
            <app-button
              v-bind="props"
              data-locator="zoom-in-button"
              type='icon'
              size="small"
              @click="zoomIn()"
            >
              <app-icon-ds
                icon="circle-plus"
                size="xs"
              />
            </app-button>
          </template>
          <span>{{ $t('common.DocumentPdfViewer.zoomIn') }}</span>
        </AppTooltip>
      </slot>
    </Teleport>
    <Teleport v-if="toolbarContainerElement" :to="toolbarContainerElement">
      <slot name="toolbar-append" :instance="getPdfAppInstance()"></slot>
    </Teleport>
    <Teleport v-if="sidebarContentHeaderElement && $vuetify.display.mdAndUp" :to="sidebarContentHeaderElement">
      <slot name="sidebar-content-header" :instance="getPdfAppInstance()">
        <h3> {{ $t('common.DocumentPdfViewer.sidebarContentHeader') }} </h3>
      </slot>
    </Teleport>
  </div>
</template>

<script>
import 'pdfjs-viewer-element'

import axios from 'axios'

import AppSelect from '@/common/AppSelect.vue'
import AppTextField from '@/common/AppTextField.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import * as disabledKeyCodes from '@/common/keyboardKeyCodes'

const maxScale = 1.5
const minScale = 0.5
const scaleIncrement = 0.25

function copyCss () {
  let cssStyles = ''

  // We cannot use index 0 because it's the browser's user agent stylesheet
  for (let i = 1; i < document.styleSheets.length; i++) {
    let style = null

    try {
      if (document.styleSheets[i]) {
        const classes =
          document.styleSheets[i].cssRules
        if (classes) {
          style = classes
        }
      }
      for (const item in style) {
        if (style[item].cssText != undefined) {
          cssStyles += style[item].cssText
        }
      }
    } catch (e) {
      console.error('Error copying CSS styles:', e)
      // We don't need to handle any errors here
      continue
    }
  }

  return cssStyles
}

// This needs to be non-reactive otherwise pdfjs gets object scope problems
let pdfAppInstance = null

// We have to put it in public and not bundled in src
// Otherwise pdfjs-viewer-element will not be able to load it properly
const viewerPath = `${import.meta.env.BASE_URL}pdfjs-${__APP_VERSION__}`

export default {
  name: 'DocumentPdfViewer',
  components: {
    AppSelect,
    AppTextField,
    AppTooltip,
  },
  emits: [
    'initialized',
    'pages-loaded',
    'page-changing',
    'scale-changing',
    'selection-changing',
    'load-file-from-pdfjs-error',
  ],
  props: {
    mnemo: {
      type: String,
      required: true,
    },
    fileId: {
      type: [String, Number],
      required: true,
    },
    menuOffset: {
      type: [Number, Array],
      default: 0,
    },
    menuTransition: {
      type: Boolean,
      default: true,
    },
  },
  data () {
    return {
      initialized: false,
      viewerPath,
      leftToolbarElement: null,
      middleToolbarElement: null,
      rightToolbarElement: null,
      toolbarContainerElement: null,
      sidebarContentHeaderElement: null,
      currentCss: '',
      zoomLevelItems: [],
      currentPageInternalValue: 1,
      zoomInternalValue: 'auto',
      pageMode: null,
      pageCount: 0,
      selection: '',
    }
  },
  computed: {
    zoomLevel: {
      get () {
        return this.zoomInternalValue
      },
      set (value) {
        this.zoomInternalValue = value === 'auto'
          ? value
          : Math.round(value)
        this.setZoomLevel(value)
      },
    },
    currentPage: {
      get () {
        return this.currentPageInternalValue
      },
      set (value) {
        value = Number(value)
        this.currentPageInternalValue = value
        this.setPage(value)
      },
    },
  },
  created () {
    this.zoomLevelItems = this.generateZoomLevels()
  },
  async mounted () {
    this.currentCss = copyCss()
    // Safari-specific: if we don't do this, the pdf will not be displayed
    // Safari has specific logic to optimize away the initialization of DOM that is out of the viewport
    // We want to keep the element out of bounds at first, so without this, we cannot work under Safari
    this.$refs.pdfElem.scrollIntoView()
    pdfAppInstance = await this.$refs.pdfElem.initialize()
    if (pdfAppInstance) {
      pdfAppInstance.eventBus.on('pagechanging', (e) => {
        this.currentPageInternalValue = e.pageNumber
        this.$emit('page-changing', e.pageNumber)
      })
      pdfAppInstance.eventBus.on('scalechanging', (e) => {
        if (e.presetValue) {
          this.zoomInternalValue = e.presetValue
        } else {
          this.zoomInternalValue = Math.round(e.scale * 100)
        }
        this.$emit('scale-changing', e.scale)
      })
      pdfAppInstance.eventBus.on('pagesloaded', (e) => {
        this.initialized = true
        // Disable the default behavior that displays multiple pages when possible
        pdfAppInstance.pdfViewer.spreadMode = 0
        this.$emit('pages-loaded', e.pagesCount)
        this.pageCount = e.pagesCount
        // Otherwise, because we reset the spread mode, it would sometimes go to 2
        pdfAppInstance.page = 1
      })
      pdfAppInstance.eventBus.on('documentloadfailed', (error) => {
        console.error('Error loading PDF:', error)
        this.$emit('load-file-from-pdfjs-error', error)
      })
      pdfAppInstance.eventBus.on('sidebarviewchanged', this.onPageModeChanged)

      const iframeDocument = this.$refs.pdfElem.iframe.contentWindow.document
      this.leftToolbarElement = iframeDocument.getElementById('toolbarViewerLeft')
      this.middleToolbarElement = iframeDocument.getElementById('toolbarViewerMiddle')
      this.rightToolbarElement = iframeDocument.getElementById('toolbarRightSide')
      this.toolbarContainerElement = iframeDocument.getElementById('toolbarContainer')
      this.sidebarContentHeaderElement = iframeDocument.getElementById('sidebar-content-header')
      // Force light mode by toggling the internal class for it
      iframeDocument.documentElement.classList.add('is-light')

      const closdStylesElement = document.createElement('style')
      closdStylesElement.innerHTML = this.currentCss
      iframeDocument.head.appendChild(closdStylesElement)

      // This should be generic enough to get the font link element from the main document without hardcoding it
      const currentFontLinkElement = Array.from(document.head.children).find((el) => {
        return el.rel === 'stylesheet' && el.href.includes('font')
      })

      if (currentFontLinkElement) {
        iframeDocument.head.appendChild(currentFontLinkElement.cloneNode())
      }

      iframeDocument.addEventListener('selectionchange', () => {
        this.selection = iframeDocument.getSelection().toString()
        this.$emit('selection-changing', this.selection)
      })
      iframeDocument.addEventListener('keydown', (e) => {
        if (Object.values(disabledKeyCodes).includes(e.keyCode)) {
          e.stopPropagation()
          e.preventDefault()
        }
      })

      this.$emit('initialized', pdfAppInstance)
      this.loadFile()
    }
  },
  methods: {
    async loadFile () {
      try {
        await pdfAppInstance.open({
          url: `${axios.defaults.baseURL}/room/${this.mnemo}/view/file/${this.fileId}`,
          httpHeaders: {
            Authorization: axios.defaults.headers.common.Authorization,
          },
        })
      } catch (error) {
        console.error('Error loading PDF:', error)
        this.$emit('load-file-from-pdfjs-error', error)
      }
    },
    getPdfAppInstance () {
      return pdfAppInstance
    },
    togglePageMode () {
      pdfAppInstance.pdfSidebar.toggle()
    },
    onPageModeChanged (event) {
      const mode = event.view
      this.pageMode = mode === 1 ? 'thumbs' : null
    },
    onViewportChanged () {
      if (pdfAppInstance && this.zoomLevel === 'auto') {
        // This will trigger recalculations on pdf.js's side
        this.setZoomLevel('auto')
      }
    },
    generateZoomLevels () {
      const zoomLevels = [
        {
          title: this.$t('common.DocumentPdfViewer.autoZoom'),
          value: 'auto',
        },
      ]
      for (let i = minScale; i <= maxScale; i += scaleIncrement) {
        zoomLevels.push({
          title: Math.round(i * 100),
          value: Math.round(i * 100),
        })
      }
      return zoomLevels
    },
    setZoomLevel (zoomLevel) {
      if (zoomLevel === 'auto') {
        pdfAppInstance.pdfViewer.currentScaleValue = zoomLevel
      } else {
        pdfAppInstance.pdfViewer.currentScaleValue = (zoomLevel / 100).toFixed(2)
      }
    },
    setPageFromEvent (event) {
      const pageNumber = Number(event.target.value)
      if (pageNumber > 0 && pageNumber <= this.pageCount) {
        this.setPage(pageNumber)
      } else {
        this.currentPage = pdfAppInstance.pdfViewer.currentPageNumber
      }
    },
    setPage (pageNumber) {
      pdfAppInstance.pdfViewer.currentPageNumber = pageNumber
    },
    zoomIn () {
      this.changeZoomLevel(true)
    },
    zoomOut () {
      this.changeZoomLevel(false)
    },
    changeZoomLevel (increase) {
      let currentZoom = parseFloat(pdfAppInstance.pdfViewer.currentScaleValue) || 1

      if (increase) {
        currentZoom = Math.min(currentZoom + scaleIncrement, maxScale)
      } else {
        currentZoom = Math.max(currentZoom - scaleIncrement, minScale)
      }

      this.zoomLevel = Math.round(currentZoom * 100)
    },
  },
}
</script>

<style scoped>
.out-of-bounds {
  transform: translateX(110%);
}
.pdf-container {
  transition: opacity 1s ease;
  opacity: 1;
  /* The height needs to substract the height of the outer title container that is outside the iframe  */
  height: calc(100% - 64px);
}

.pdf-container.loading {
  opacity: 0;
}

.pdf-viewer {
  position: relative;
  width: 100%;
  height: 100%;
}
</style>
